import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  matchTaxAdjustmentOption,
  SnapshotReviewTable
} from "./ColumnSelectionReviewStep";
import BaseDialog from "../../utils/dialog/BaseDialog";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ApplyingLoading from "../../Common/ClassificationEngine/ApplyingLoading";

const APPLY_SECTIONS = ["Revenue", "Other Income", "Cost of Sales", "Expenses"];
const IGNORE_SECTION = "Ignore";
const SENSITIVE_MARKUP_PATTERN = /<[^<>]+>/;

// Paint the loading state before apply can perform synchronous P&L updates.
const waitForLoadingPaint = () =>
  new Promise((resolve) => {
    let frame;
    const finish = () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
      resolve();
    };
    // Animation frames may be suspended in a background tab.
    const timeout = setTimeout(finish, 100);
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(finish);
    });
  });

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const hasSensitiveMarkup = (value) =>
  SENSITIVE_MARKUP_PATTERN.test(String(value ?? ""));

const fieldLabel = (field) => {
  const labels = {
    name: "Account Name",
    amount: "Amount",
    section: "Section",
    taxAdjustment: "Tax Adjustment",
    accountCode: "Account Code",
    reference: "Reference"
  };
  return labels[field] || field;
};

const toChildApplyRow = (parent, child) => ({
  row_id: child.row_id,
  name: child.name ?? "",
  amount: child.amount,
  accountCode: child.accountCode ?? "",
  taxAdjustment: child.taxAdjustment ?? "",
  reference: parent.reference ?? "",
  section: parent.section ?? "",
  history: []
});

const flattenApplyRows = (rows = []) => {
  const ignored = [];
  const candidates = [];

  rows.forEach((row, index) => {
    if (row.section === IGNORE_SECTION) {
      ignored.push(row);
      return;
    }

    if (row.unmerged) {
      const children =
        (row.childRows || []).length > 0
          ? row.childRows
          : (row.history || []).map((item, childIndex) => ({
              row_id: item.row_id || `${row.row_id}-child-${childIndex}`,
              name: item.name ?? "",
              amount: item.amount,
              accountCode: item.accountCode ?? "",
              taxAdjustment: item.taxAdjustment ?? row.taxAdjustment ?? ""
            }));
      children.forEach((child) => {
        candidates.push({
          row: toChildApplyRow(row, child),
          sourceLabel: `${row.name || `Row ${index + 1}`} → ${
            child.name || "Untitled"
          }`
        });
      });
      return;
    }

    candidates.push({
      row,
      sourceLabel: row.name || `Row ${index + 1}`
    });
  });

  return { ignored, candidates };
};

const collectUnmatchedFields = (row, taxAdjustmentOptions) => {
  const unmatched = [];

  if (isBlank(row.name) && isBlank(row.accountName)) {
    unmatched.push({ field: "name", detail: "missing" });
  } else if (hasSensitiveMarkup(row.name || row.accountName)) {
    unmatched.push({
      field: "name",
      detail: "contains redacted sensitive data"
    });
  }

  if (row.amount === "" || row.amount === null || row.amount === undefined) {
    unmatched.push({ field: "amount", detail: "missing" });
  } else if (!Number.isFinite(Number(row.amount))) {
    unmatched.push({ field: "amount", detail: "not a valid number" });
  }

  if (isBlank(row.section)) {
    unmatched.push({ field: "section", detail: "missing" });
  } else if (!APPLY_SECTIONS.includes(row.section)) {
    unmatched.push({
      field: "section",
      detail: `"${row.section}" is not a P&L section`
    });
  }

  const taxValue = String(row.taxAdjustment ?? "").trim();
  if (taxValue && taxValue !== "None") {
    const matched = matchTaxAdjustmentOption(taxValue, taxAdjustmentOptions);
    if (!matched || matched.value === undefined) {
      unmatched.push({
        field: "taxAdjustment",
        detail: `"${taxValue}" does not match a tax adjustment option`
      });
    }
  }

  if (hasSensitiveMarkup(row.accountCode)) {
    unmatched.push({
      field: "accountCode",
      detail: "contains redacted sensitive data"
    });
  }
  if (hasSensitiveMarkup(row.reference)) {
    unmatched.push({
      field: "reference",
      detail: "contains redacted sensitive data"
    });
  }

  return unmatched;
};

const normalizeTaxValue = (value, taxAdjustmentOptions) => {
  const matched = matchTaxAdjustmentOption(value, taxAdjustmentOptions);
  return matched?.value ?? "";
};

const buildApplyPreview = (classifiedRows, taxAdjustmentOptions) => {
  const { ignored, candidates } = flattenApplyRows(classifiedRows);
  const errors = [];
  const snapshotRows = [];
  const validRows = [];
  const redacted = { fieldCount: 0, rowCount: 0 };

  candidates.forEach(({ row, sourceLabel }) => {
    const unmatched = collectUnmatchedFields(row, taxAdjustmentOptions);
    const redactedFields = [
      row.name || row.accountName,
      row.accountCode,
      row.reference
    ].filter(hasSensitiveMarkup);
    if (redactedFields.length) {
      redacted.rowCount += 1;
      redacted.fieldCount += redactedFields.length;
    }
    const snapshotRow = {
      accountCode: row.accountCode ?? "",
      accountName: row.name || row.accountName || "",
      reference: row.reference ?? "",
      amount: row.amount,
      taxAdjustment: row.taxAdjustment ?? "",
      section: row.section
    };
    snapshotRows.push(snapshotRow);

    if (unmatched.length) {
      errors.push({
        label: sourceLabel,
        fields: unmatched.map(
          (item) => `${fieldLabel(item.field)} (${item.detail})`
        )
      });
      return;
    }

    const history = Array.isArray(row.history) ? row.history : [];
    validRows.push({
      ...snapshotRow,
      amount: Number(row.amount),
      taxAdjustment: normalizeTaxValue(row.taxAdjustment, taxAdjustmentOptions),
      history: history.map((item) => ({
        name: item.name ?? "",
        amount: Number(item.amount) || 0,
        accountCode: item.accountCode ?? "",
        taxAdjustment: normalizeTaxValue(
          item.taxAdjustment,
          taxAdjustmentOptions
        )
      }))
    });
  });

  const rowsBySection = APPLY_SECTIONS.reduce((acc, section) => {
    acc[section] = validRows.filter((row) => row.section === section);
    return acc;
  }, {});

  return {
    ignoredCount: ignored.length,
    applyRows: validRows,
    snapshotRows,
    rowsBySection,
    errors,
    redacted,
    canApply: errors.length === 0 && validRows.length > 0
  };
};

const ApplyModeCard = ({ selected, value, title, description, disabled }) => (
  <FormControlLabel
    value={value}
    disabled={disabled}
    control={<Radio sx={{ p: 1, flexShrink: 0 }} />}
    sx={{
      m: 0,
      p: 1.5,
      flex: 1,
      minWidth: 0,
      alignItems: "flex-start",
      border: "1px solid",
      borderRadius: 2,
      borderColor: selected ? "primary.main" : "divider",
      bgcolor: (theme) =>
        selected ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
      opacity: disabled ? 0.65 : 1,
      "&:focus-within": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: 2
      },
      "& .MuiFormControlLabel-label": { minWidth: 0, pt: 0.75 }
    }}
    label={
      <Box>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography color='text.secondary' sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
    }
  />
);

const ApplySnapshotDialog = ({
  open,
  onClose,
  classifiedRows = [],
  classifyOptions,
  currency,
  taxAdjustmentOptions = [],
  existingSectionCounts = {},
  existingTaggedCount = 0,
  applying = false,
  onConfirm
}) => {
  const [applyMode, setApplyMode] = useState("add");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applyError, setApplyError] = useState("");
  const submittingRef = useRef(false);
  const mountedRef = useRef(false);
  const openRef = useRef(open);
  const busy = applying || isSubmitting;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    openRef.current = open;
    if (open) {
      setApplyMode("add");
      setApplyError("");
    }
  }, [open]);
  const preview = useMemo(
    () => buildApplyPreview(classifiedRows, taxAdjustmentOptions),
    [classifiedRows, taxAdjustmentOptions]
  );
  const existingTotal = APPLY_SECTIONS.reduce(
    (sum, section) => sum + (existingSectionCounts[section] || 0),
    0
  );
  const confirmDisabled =
    busy ||
    !onConfirm ||
    !preview.canApply ||
    !applyMode ||
    preview.applyRows.length === 0;

  const handleClose = () => {
    if (busy || submittingRef.current) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (confirmDisabled || submittingRef.current) return;
    submittingRef.current = true;
    flushSync(() => {
      setApplyError("");
      setIsSubmitting(true);
    });
    try {
      await waitForLoadingPaint();
      if (!mountedRef.current || !openRef.current) return;
      await onConfirm(applyMode, preview.rowsBySection, {
        ignoredCount: preview.ignoredCount,
        appliedCount: preview.applyRows.length
      });
    } catch (error) {
      if (mountedRef.current) {
        setApplyError(
          error?.message || "Accounts could not be applied. Please try again."
        );
      }
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const currencyLabel =
    currency === "MYR" ? "RM" : currency === "SGD" ? "S$" : currency;
  const countLabel = (count, label) =>
    `${count} ${label}${count === 1 ? "" : "s"}`;

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      closeDisabled={busy}
      icon={<FactCheckOutlinedIcon />}
      title='Apply to Detailed P&L'
      maxWidth='lg'
      paperSx={{
        m: { xs: 1, sm: 2 },
        width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
        height: "calc(100dvh - 32px)",
        maxHeight: "calc(100dvh - 32px)",
        minHeight: 0,
        overflow: "hidden"
      }}
      contentSx={{
        ...(busy && {
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }),
        minHeight: 0,
        minWidth: 0,
        p: { xs: 2, sm: 3 },
        overflowY: "auto",
        "& .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-subtitle2":
          { fontSize: 16, lineHeight: 1.5 },
        "& .MuiTableContainer-root": {
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none"
        },
        "& .MuiTableCell-root": {
          fontSize: 16,
          px: 2,
          py: 1.5,
          verticalAlign: "middle",
          whiteSpace: "normal",
          overflowWrap: "anywhere"
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          bgcolor: "background.default",
          color: "text.primary",
          fontWeight: 700,
          textTransform: "none",
          letterSpacing: 0
        },
        "& .MuiTableCell-alignRight": { whiteSpace: "nowrap" },
        "& :focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2
        }
      }}
      actionsSx={{
        py: 1.5,
        px: { xs: 2, sm: 3 },
        flexShrink: 0,
        gap: 1,
        flexWrap: "wrap",
        "& .MuiButton-root": {
          minHeight: 44,
          fontSize: 16,
          textTransform: "none",
          px: { xs: 2, sm: 3 },
          flex: { xs: "1 1 auto", sm: "0 0 auto" },
          m: 0
        }
      }}
      actions={
        <>
          <Button variant='outlined' onClick={handleClose} disabled={busy}>
            Back to review
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {busy
              ? "Applying..."
              : applyMode === "replace"
                ? "Confirm replace"
                : "Confirm add"}
          </Button>
        </>
      }
    >
      {busy ? (
        <ApplyingLoading
          rowCount={preview.applyRows.length}
          mode={applyMode}
          itemLabel='account'
          targetLabel='Detailed P&L'
        />
      ) : (
        <Stack spacing={3}>
          {applyError && <Alert severity='error'>{applyError}</Alert>}
          <Typography color='text.secondary'>
            Review these accounts, then choose how to apply them.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))"
              },
              gap: 2
            }}
          >
            {[
              {
                label: "Accounts to apply",
                count: preview.applyRows.length,
                highlight: true
              },
              { label: "Excluded rows", count: preview.ignoredCount },
              { label: "Need attention", count: preview.errors.length }
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 2,
                  minWidth: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: item.highlight
                    ? (theme) => alpha(theme.palette.primary.main, 0.05)
                    : "background.paper",
                  display: { xs: "flex", sm: "block" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2
                }}
              >
                <Typography fontWeight={600}>{item.label}</Typography>
                <Typography
                  variant='h5'
                  fontWeight={700}
                  sx={{ mt: { xs: 0, sm: 0.75 } }}
                >
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>

          {preview.errors.length > 0 && (
            <Box>
              <Alert severity='error' sx={{ mb: 1.5 }}>
                Return to review and fix these fields before applying.
              </Alert>
              <TableContainer
                role='region'
                aria-label='Fields to fix'
                tabIndex={0}
                sx={{ maxHeight: 280 }}
              >
                <Table
                  stickyHeader
                  aria-label='Fields to fix'
                  sx={{ minWidth: 480 }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>What to fix</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.errors.map((error, index) => (
                      <TableRow key={`${error.label}-${index}`}>
                        <TableCell component='th' scope='row'>
                          {error.label}
                        </TableCell>
                        <TableCell>
                          {error.fields.map((field) => (
                            <Typography key={field}>{field}</Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {preview.snapshotRows.length === 0 && preview.errors.length === 0 && (
            <Alert severity='warning'>
              No accounts to apply. Return to review to check the account
              sections.
            </Alert>
          )}

          <Box>
            <Typography
              component='h3'
              variant='h6'
              sx={{ fontSize: 18, fontWeight: 700, mb: 1.5 }}
              id='apply-mode-heading'
            >
              Choose how to apply
            </Typography>
            <RadioGroup
              name='apply-mode'
              aria-labelledby='apply-mode-heading'
              value={applyMode}
              onChange={(_event, value) => setApplyMode(value)}
              sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
            >
              <ApplyModeCard
                value='add'
                selected={applyMode === "add"}
                disabled={!preview.canApply || busy}
                title='Add to existing P&L'
                description={`Keep ${countLabel(existingTotal, "existing account")}. Add ${countLabel(preview.applyRows.length, "new account")}.`}
              />
              <ApplyModeCard
                value='replace'
                selected={applyMode === "replace"}
                disabled={!preview.canApply || busy}
                title='Replace current P&L'
                description={`Remove ${countLabel(existingTotal, "existing account")}. Use these ${countLabel(preview.applyRows.length, "account")} instead.`}
              />
            </RadioGroup>
            {applyMode === "replace" && existingTaggedCount > 0 && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                Replacing will unlink{" "}
                {countLabel(existingTaggedCount, "tagged schedule")} from the
                current P&amp;L accounts.
              </Alert>
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component='h3'
              variant='h6'
              sx={{ fontSize: 18, fontWeight: 700, mb: 1.5 }}
            >
              Account preview{currencyLabel ? ` (${currencyLabel})` : ""}
            </Typography>
            {preview.ignoredCount > 0 && (
              <Typography color='text.secondary' sx={{ mb: 1.5 }}>
                Original subtotals and rows marked Ignore are excluded.
              </Typography>
            )}
            <SnapshotReviewTable
              rows={preview.snapshotRows}
              taxAdjustmentOptions={taxAdjustmentOptions}
              processingOptions={classifyOptions}
              variant='apply'
              showProcessingSummary={false}
            />
          </Box>
        </Stack>
      )}
    </BaseDialog>
  );
};

export { APPLY_SECTIONS, buildApplyPreview };
export default ApplySnapshotDialog;
